import type { RegisteredTool } from "../tools/core/types.js";
import { createBackgroundTools } from "./tools/background/index.js";
import { createDocumentTools } from "./tools/documents/index.js";
import { createNetworkTools } from "./tools/network/index.js";
import { createMediaTools } from "./tools/media/index.js";
import { createSkillTools } from "./tools/skills/index.js";
import { createTodoTools } from "./tools/todo/index.js";
import { createSchedulerTools } from "./tools/scheduler/index.js";
import { createWorktreeTools } from "./tools/worktree/index.js";

type ExtensionCapabilityCost = "low" | "medium" | "high";

export interface ExtensionDefinition {
  id: string;
  envKey: `KITTY_EXTENSION_${string}`;
  defaultEnabled: boolean;
  summary: string;
  createTools: () => readonly RegisteredTool[];
  capability: {
    description: string;
    bestFor: readonly string[];
    cost: ExtensionCapabilityCost;
  };
}

export const EXTENSION_DEFINITIONS = [
  {
    id: "todo",
    envKey: "KITTY_EXTENSION_TODO",
    defaultEnabled: true,
    summary: "Session todo writing and visible checklist preview.",
    createTools: createTodoTools,
    capability: {
      description: "Session-level todo checklist writing and visible progress preview.",
      bestFor: [
        "maintaining current session checklist state",
        "showing concise progress preview",
      ],
      cost: "low",
    },
  },
  {
    id: "scheduler",
    envKey: "KITTY_EXTENSION_SCHEDULER",
    defaultEnabled: true,
    summary: "Durable machine-driven reminders and prewritten command schedules.",
    createTools: createSchedulerTools,
    capability: {
      description: "Create, list, update, and delete durable scheduled reminders or prewritten commands without model polling.",
      bestFor: [
        "one-time reminders",
        "recurring local commands with explicit intervals or daily times",
        "managing durable schedules and reading trigger results",
      ],
      cost: "low",
    },
  },
  {
    id: "worktree",
    envKey: "KITTY_EXTENSION_WORKTREE",
    defaultEnabled: true,
    summary: "Git worktree discovery and lifecycle management.",
    createTools: createWorktreeTools,
    capability: {
      description: "Git worktree discovery and lifecycle management.",
      bestFor: [
        "inspecting git worktrees",
        "creating and removing explicit worktree paths",
      ],
      cost: "medium",
    },
  },
  {
    id: "network",
    envKey: "KITTY_EXTENSION_NETWORK",
    defaultEnabled: true,
    summary: "HTTP session, request, probe, download, trace, and OpenAPI tools.",
    createTools: createNetworkTools,
    capability: {
      description: "HTTP session, request, probe, suite, download, trace, and OpenAPI inspection tools.",
      bestFor: [
        "probing HTTP services",
        "running structured HTTP request suites",
        "recording network evidence",
      ],
      cost: "medium",
    },
  },
  {
    id: "background",
    envKey: "KITTY_EXTENSION_BACKGROUND",
    defaultEnabled: true,
    summary: "Background command execution with durable lifecycle tracking.",
    createTools: createBackgroundTools,
    capability: {
      description: "Background command execution with control-plane lifecycle tracking, output read, stop, wait, and wake facts.",
      bestFor: [
        "running long local commands without blocking the current turn",
        "reading, checking, waiting for, or terminating recorded background executions",
        "watching services, test matrices, or commands that produce output over time",
      ],
      cost: "medium",
    },
  },
  {
    id: "documents",
    envKey: "KITTY_EXTENSION_DOCUMENTS",
    defaultEnabled: true,
    summary: "Bounded DOCX and text-based PDF reading plus Word document writing.",
    createTools: createDocumentTools,
    capability: {
      description: "Read DOCX and text-based PDF content in ranges, and create Word DOCX files.",
      bestFor: [
        "extracting text from local Word and PDF documents",
        "creating Word documents from plain text or simple Markdown",
      ],
      cost: "medium",
    },
  },
  {
    id: "media",
    envKey: "KITTY_EXTENSION_MEDIA",
    defaultEnabled: true,
    summary: "Configured image generation and durable asynchronous video workflows.",
    createTools: createMediaTools,
    capability: {
      description: "Generate local image artifacts and create or poll durable Agnes video tasks.",
      bestFor: [
        "text-to-image and image editing",
        "asynchronous text-to-video and image-to-video generation",
        "saving generated media as project files",
      ],
      cost: "high",
    },
  },
  {
    id: "skills",
    envKey: "KITTY_EXTENSION_SKILLS",
    defaultEnabled: true,
    summary: "Project runtime skill discovery and explicit loading.",
    createTools: createSkillTools,
    capability: {
      description: "Project runtime skill discovery and explicit SKILL.md loading without automatic routing.",
      bestFor: [
        "listing project skills",
        "loading a specialized project method only when the model chooses it",
      ],
      cost: "low",
    },
  },
] as const satisfies readonly ExtensionDefinition[];

export type ExtensionId = (typeof EXTENSION_DEFINITIONS)[number]["id"];

export const EXTENSION_IDS = EXTENSION_DEFINITIONS.map((definition) => definition.id) as ExtensionId[];

export const EXTENSION_ENV_KEYS = Object.fromEntries(
  EXTENSION_DEFINITIONS.map((definition) => [definition.id, definition.envKey]),
) as Record<ExtensionId, (typeof EXTENSION_DEFINITIONS)[number]["envKey"]>;

export function getExtensionDefinition(id: ExtensionId): (typeof EXTENSION_DEFINITIONS)[number] {
  return EXTENSION_DEFINITIONS.find((definition) => definition.id === id) ?? missingExtensionDefinition(id);
}

function missingExtensionDefinition(id: ExtensionId): never {
  throw new Error(`Unknown extension definition: ${String(id)}`);
}
